// lib
import React, { useState, useCallback, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

import copyToClipboard from '@aytm/lib/common/copy-to-clipboard'
import { calcUidFilter, PINNED_STATUS_CLAUSE } from '@aytm/app/stats/lib/query/uid-filter'

// components
import { Button } from 'primereact/button'

// styles
import style from '@aytm/app/stats/page/sidebar/filters/styles/FiltersPanel.module.scss'

// At or below DISPLAY_LIMIT the uid-pinned filter expression is shown in a text box; above it (up to the pin
// ceiling) only a Copy button is offered (the expression is too long to display). The uids arrive with the main
// report load, capped at the pin ceiling; `uidsTruncated` flags that the true match set exceeds it (refuse to pin).
const DISPLAY_LIMIT = 50

const FiltersUids = ({ uids, uidsTruncated }) => {
  const [shown, setShown] = useState(false)
  const [copied, setCopied] = useState(false)
  const [copyFailed, setCopyFailed] = useState(false)

  const copyTimer = useRef(null)
  const mounted = useRef(true)
  // re-armed in the body, not just cleared in the teardown: a remount reuses the ref, so under a
  // double-invoked mount the guard would otherwise stay false and swallow every later setState
  useEffect(() => {
    mounted.current = true

    return () => {
      mounted.current = false
      clearTimeout(copyTimer.current)
    }
  }, [])

  const onToggle = useCallback(() => {
    setCopyFailed(false)
    setShown(prev => !prev)
  }, [])

  // copies the uid-pinned filter expression (never the raw uid list); a failed write must not confirm,
  // and reveals the expression so the over-limit band keeps a manual route to it
  const onCopyFilter = useCallback(async () => {
    const ok = await copyToClipboard(calcUidFilter(uids, PINNED_STATUS_CLAUSE))
    if (!mounted.current) return

    setCopyFailed(!ok)
    if (!ok) return

    setCopied(true)
    clearTimeout(copyTimer.current)
    copyTimer.current = setTimeout(() => setCopied(false), 2000)
  }, [uids])

  return (
    <div className={style['uids-container']}>
      <span
        className={style['uids-link']}
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={event => ('Enter' == event.key || ' ' == event.key) && (event.preventDefault(), onToggle())}
      >
        {shown ? 'Hide UUIDs' : 'Show UUIDs'}
      </span>

      {shown &&
        (uidsTruncated ? (
          <div className={style['uids-notice']}>Too many respondents to pin. Narrow the filter and try again.</div>
        ) : 0 < uids.length ? (
          <>
            {(uids.length <= DISPLAY_LIMIT || copyFailed) && (
              <textarea className={style['uids-textarea']} readOnly value={calcUidFilter(uids, PINNED_STATUS_CLAUSE)} />
            )}
            <Button
              className="p-button-rounded"
              icon={copied ? 'pi pi-check' : null}
              label={copied ? 'Copied!' : `Copy ${uids.length} UUID${1 < uids.length ? 's' : ''} as a filter`}
              onClick={onCopyFilter}
            />
            {copyFailed && (
              <div className={style['uids-notice']}>
                Couldn&apos;t reach your clipboard. Select the text above and copy it by hand.
              </div>
            )}
          </>
        ) : (
          <div className={style['uids-notice']}>No matching respondents.</div>
        ))}
    </div>
  )
}

FiltersUids.propTypes = {
  uids: PropTypes.array,
  uidsTruncated: PropTypes.bool
}

FiltersUids.displayName = 'FiltersUids'

export default FiltersUids
